import { useRef, useState, useEffect, useMemo } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import Message from '../components/Message';
import PageHeader from '../components/PageHeader';
import Loader from '../components/Loader';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useLazyGetProductDetailsQuery } from '../slices/productsApiSlice';
import { addToCart } from "../slices/cartSlice";
import { convertToEAN13 } from '../utils/barcodeUtils';
import { isValidIsbn, isbn10to13 } from '../utils/validation';
import { BrowserMultiFormatReader } from '@zxing/browser';
import AlertModal from '../components/AlertModal';
import { toast } from 'react-toastify';

const BarcodeScreen = () => {
  const video = useRef(null);
  const canvas = useRef(null);
  const scanCanvas = useRef(document.createElement('canvas'));
  const barcodeRef = useRef('');
  const streamRef = useRef(null);
  const scanFrameRef = useRef(null);
  const [barcode, setBarcode] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [lookupPending, setLookupPending] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [alertModal, setAlertModal] = useState({
    show: false,
    message: "",
    variant: "danger",
  });

  const showAlert = (message, variant = "danger") => {
    setAlertModal({ show: true, message, variant });
  };

  
  const cleanedBarcode = barcode?.trim();
  const [fetchProductDetails, { currentData: offer, isError, isFetching, isLoading }] = useLazyGetProductDetailsQuery();

//   const fallbackReader = new BrowserMultiFormatReader();

  const { userInfo } = useSelector((state) => state.auth);
  const { cartItems } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const finalOffer = useMemo(() => {
    if (!offer || !cleanedBarcode || !isValidIsbn(cleanedBarcode)) return null;
    return { ...offer, isbn: cleanedBarcode };
  }, [offer, cleanedBarcode]);

    const openCam = async () => {
    try {
      setCameraError('');
        const constraints = {
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            aspectRatio: { ideal: 16 / 9 },
          }
        };

        let mediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        }

        streamRef.current = mediaStream;
        setStream(mediaStream);
    } catch (err) {
        console.error("Error opening camera:", err);
        setCameraError(
          err.name === 'NotAllowedError'
            ? 'Cameratoegang is geblokkeerd. Sta cameratoegang toe in uw browserinstellingen en probeer opnieuw.'
            : err.name === 'NotFoundError'
              ? 'Geen camera gevonden op dit apparaat.'
              : 'De camera kon niet worden gestart. Controleer cameratoegang en probeer opnieuw.'
        );
    }
  };

  useEffect(() => {
    if (!stream || !video.current) return;

    video.current.srcObject = stream;
    video.current.play().catch((err) => {
      console.warn('Could not start camera video playback:', err);
      setCameraError('De camerabeelden konden niet worden gestart. Controleer cameratoegang en probeer opnieuw.');
    });

    return () => {
      if (video.current?.srcObject === stream) video.current.srcObject = null;
    };
  }, [stream]);

    const closeCam = () => {
    // stop camera stream
    if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        setStream(null);
    }
    // stop scanning loop
    if (scanFrameRef.current) {
        cancelAnimationFrame(scanFrameRef.current);
        scanFrameRef.current = null;
    }
    // clear video element
    if (video.current) {
        video.current.srcObject = null;
    }
    };

  useEffect(() => {
    openCam();
    return () => {
      closeCam();
    };
  }, []);

  const codeReader = useRef(new BrowserMultiFormatReader());

    useEffect(() => {
    if (!stream || !canvas.current || !video.current) return;

    const ctx = canvas.current.getContext("2d");
    let barcodeDetector;

    const scanBarcode = async () => {
    if (!stream || !canvas.current || !video.current) return;
    if (video.current.readyState !== 4) {
      scanFrameRef.current = requestAnimationFrame(scanBarcode);
      return;
    }

    const videoWidth = video.current.videoWidth;
    const videoHeight = video.current.videoHeight;
    if (!videoWidth || !videoHeight) {
      scanFrameRef.current = requestAnimationFrame(scanBarcode);
      return;
    }

    canvas.current.width = videoWidth;
    canvas.current.height = videoHeight;
    ctx.drawImage(video.current, 0, 0, videoWidth, videoHeight);

    const scanWidth = Math.min(videoWidth, videoHeight) * 0.82;
    const scanHeight = scanWidth * 0.32;
    const scanX = (videoWidth - scanWidth) / 2;
    const scanY = Math.max(60, (videoHeight - scanHeight) / 2 - 10);

    const scanCtx = scanCanvas.current.getContext("2d");
    scanCanvas.current.width = scanWidth;
    scanCanvas.current.height = scanHeight;
    scanCtx.clearRect(0, 0, scanWidth, scanHeight);
    scanCtx.drawImage(canvas.current, scanX, scanY, scanWidth, scanHeight, 0, 0, scanWidth, scanHeight);

    try {
      let scannedBarcode = null;
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      const tryDecode = async (sourceCanvas) => {
        if (!sourceCanvas) return null;

        if (!isiOS && "BarcodeDetector" in window) {
          barcodeDetector ??= new window.BarcodeDetector({ formats: ["ean_13","ean_8","upc_a"] });
          const barcodes = await barcodeDetector.detect(sourceCanvas);
          return barcodes?.[0]?.rawValue ?? null;
        }
        const result = await codeReader.current.decodeFromCanvas(sourceCanvas);
        return result?.text ?? null;
      };

      scannedBarcode = await tryDecode(scanCanvas.current);
      if (!scannedBarcode) {
        scannedBarcode = await tryDecode(canvas.current);
      }

      let shouldContinue = true;
      if (scannedBarcode) {
        let ean13Barcode = convertToEAN13(scannedBarcode);
        // If scanned is ISBN-10, convert it
        if (scannedBarcode.length === 10) {
          ean13Barcode = isbn10to13(scannedBarcode);
        }

        if (ean13Barcode && ean13Barcode !== barcodeRef.current) {
          barcodeRef.current = ean13Barcode;
          if (ean13Barcode[3] !== '9') {
            showAlert("Sorry, we kunnen dit boek niet inkopen.", "warning");
          } else if (cartItems.some((item) => item.isbn === ean13Barcode)) {
            toast.info('Dit boek staat al in uw winkelwagen.');
            setBarcode('');
          } else {
            navigator.vibrate?.(200);
            setBarcode(ean13Barcode);
            setShowModal(true);
            setLookupPending(true);
            setLookupError('');
            fetchProductDetails(ean13Barcode, false)
              .unwrap()
              .catch((error) => {
                setLookupError(error?.data?.message || error.error || 'Fout bij het ophalen van gegevens.');
              })
              .finally(() => setLookupPending(false));
            shouldContinue = false;
          }
        }
      }

      if (shouldContinue) {
        scanFrameRef.current = requestAnimationFrame(scanBarcode);
      }
      } catch (err) {
        console.warn('Barcode scan error:', err);
        scanFrameRef.current = requestAnimationFrame(scanBarcode);
      }
    };

    scanFrameRef.current = requestAnimationFrame(scanBarcode);

    return () => {
        if (scanFrameRef.current) {
          cancelAnimationFrame(scanFrameRef.current);
          scanFrameRef.current = null;
        }
    };
    }, [stream, fetchProductDetails, cartItems]);

  const restartScanning = async () => {
    closeCam();
    setShowModal(false);
    setBarcode('');
    setLookupPending(false);
    setLookupError('');
    barcodeRef.current = '';
    await openCam();
  };

  const handleCloseCamera = () => {
    closeCam();
    navigate('/');
  };

  const handleAlertClose = () => {
    setAlertModal({ show: false, message: "", variant: "danger" });
    void restartScanning();
  };

  const addToCartHandler = async () => {
    if (!finalOffer || !finalOffer.isbn) {
      showAlert("Ongeldig item", "warning");
      setShowModal(false);
      return;
    }

    const isInCart = cartItems.some((x) => x.isbn === finalOffer.isbn);
    if (isInCart) {
      toast.info('Dit boek staat al in uw winkelwagen.');
      await restartScanning();
      return;
    }

    dispatch(addToCart(finalOffer));
    toast.success('Boek toegevoegd aan de winkelwagen.');
    await restartScanning();
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <Message variant='danger'>{isError?.data?.message || isError.error}</Message>
      ) : (
        <div style={{ position: 'relative', minHeight: '100vh', width: '100%', padding: 0, overflow: 'hidden' }}>
          <video
            ref={video}
            autoPlay
            playsInline
            muted
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <canvas ref={canvas} style={{ display: 'none' }} />

          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
            <div style={{ width: '80vw', maxWidth: '460px', height: '28vw', maxHeight: '220px', border: '3px solid #fff', borderRadius: '8px', boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)', transform: 'translateY(0px)' }} />
            <div style={{ marginTop: '1rem', color: '#fff', textAlign: 'center', textShadow: '0 0 10px rgba(0,0,0,0.8)', pointerEvents: 'none' }}>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Place the barcode inside the frame</p>
              <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9 }}>Keep the camera steady for best results</p>
            </div>
          </div>

          {cameraError && (
            <div className="alert alert-warning position-absolute top-50 start-50 translate-middle text-center" style={{ zIndex: 30, width: 'min(90vw, 460px)' }} role="alert">
              <p className="mb-3">{cameraError}</p>
              <Button variant="primary" onClick={() => void openCam()} style={{ pointerEvents: 'auto' }}>
                Probeer opnieuw
              </Button>
            </div>
          )}

          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20, padding: '0.75rem' }}>
            <div style={{ borderRadius: '12px', padding: '0.5rem' }}>
              <PageHeader 
                totalBooks={cartItems.length} 
                totalWeight={0} 
                totalPrice={cartItems.reduce((acc, item) => acc + parseFloat(item.offer_price || 0), 0).toFixed(2)} 
              />

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                <Button
                  variant="danger"
                  onClick={handleCloseCamera}
                  className="text-white"
                  aria-label="Close camera"
                  title="Close camera"
                >
                  <FaTimes aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>

          <AlertModal
            show={alertModal.show}
            onClose={handleAlertClose}
            message={alertModal.message}
            variant={alertModal.variant}
          />

          <Modal 
            show={showModal} 
            onHide={() => {
              void restartScanning();
            }}>
            <Modal.Header closeButton>
              <Modal.Title>Toevoegen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {lookupPending || isFetching ? (
                <div className="text-center"><Loader /></div>
              ) : lookupError ? (
                <p className="text-danger">{lookupError}</p>
              ) : isError ? (
                <p className="text-danger">Fout bij het ophalen van gegevens.</p>
              ) : finalOffer ? (
                <div>
                  <h5><strong>Title:</strong> {finalOffer.title || "Onbekend"}</h5>
                  <p><strong>ISBN:</strong> {finalOffer.isbn || "Onbekend"}</p>
                  <p><strong>Author:</strong> {finalOffer.author || "Onbekend"}</p>
                  {finalOffer.offer_price > 0 ? (
                    <>
                      <p className='text-center'>Our offer:</p>
                      <h4 className='text-center'><strong>€{finalOffer.offer_price}</strong></h4>
                    </>
                  ) : (
                    <p className='text-center'><strong>Sorry, we can't buy it.</strong></p>
                  )}
                  {userInfo?.isAdmin && (
                    <>
                      <p><strong>Source:</strong> {finalOffer.source}</p>
                      <p><strong>Message:</strong> {finalOffer.message}</p>
                    </>
                  )}
                </div>
              ) : (
                <p>Boek niet herkend.</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  void restartScanning();
                }}
                style={{ backgroundColor: "#6c757d", borderColor: "#6c757d", color: "white" }}
              >
                {!lookupPending && !isFetching && !lookupError && finalOffer?.offer_price > 0 ? "Annuleren" : "Sluiten"}
              </Button>
              {!lookupPending && !isFetching && !lookupError && finalOffer?.offer_price > 0 && (
                <Button
                  variant="success"
                  style={{ backgroundColor: "#28a745", borderColor: "#28a745", color: "white" }}
                  onClick={addToCartHandler}
                >
                  Toevoegen
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </>
  );
};

export default BarcodeScreen;